import {
  Artboard,
  File,
  RiveCanvas as RiveCanvasType,
  SMIInput,
  StateMachineInstance,
} from '@rive-app/canvas-advanced';
import sortBy from 'lodash/sortBy';
import uniq from 'lodash/uniq';

import { Animation } from '@repo/database-core';
import { LoadedRiveAsset, RiveFileLoaderOptions } from '@repo/types-client';

/**
 * Extended interface for StateMachineInstance with hair customization inputs.
 *
 * This interface adds optional properties for hair color and hair pinch inputs
 * that are specific to hair-type animations in the avatar system.
 *
 * @property {SMIInput | null} [hairColorInput] - Input for controlling hair color
 * @property {SMIInput | null} [hairPinchInput] - Input for controlling hair style/pinch
 */
interface ExtendedStateMachineInstance extends StateMachineInstance {
  hairColorInput?: SMIInput | null;
  hairPinchInput?: SMIInput | null;
}

/**
 * Manages loading, caching, and organizing Rive animation files for the avatar system.
 *
 * This class handles:
 * - Loading Rive files from URLs
 * - Caching loaded files to prevent duplicate loading
 * - Managing artboards and state machines
 * - Organizing animation layers by z-index
 * - Cleaning up resources when no longer needed
 *
 * It supports different types of animations (BODY, HAIR, etc.) and handles
 * their specific initialization requirements.
 */
export default class RiveFileLoader {
  /** The Rive canvas instance used for loading and managing animations */
  private rive: RiveCanvasType;
  /** Collection of loaded Rive files */
  private loadedFiles: File[] = [];
  /** Collection of loaded artboards from the files */
  private loadedArtboards: Artboard[] = [];
  /** Collection of loaded state machines with extended properties */
  private loadedStateMachines: ExtendedStateMachineInstance[] = [];
  /** All loaded animation layers */
  public loadedLayers: LoadedRiveAsset[] = [];
  /** Animation layers sorted by z-index for proper rendering order */
  public sortedLayers: LoadedRiveAsset[] = [];
  /** Cache to prevent reloading the same file multiple times */
  private fileCache = new Map<string, LoadedRiveAsset>();

  /**
   * Creates a new RiveFileLoader instance.
   *
   * @param {RiveFileLoaderOptions} options - Configuration options
   * @param {RiveCanvasType} options.rive - The Rive canvas instance to use
   */
  constructor({ rive }: RiveFileLoaderOptions) {
    this.rive = rive;
  }

  /**
   * Fetches and loads a Rive file from a URL.
   *
   * @param {string} path - URL path to the Rive file
   * @returns {Promise<File>} Promise resolving to the loaded Rive file
   * @private
   */
  private async fetchAndLoadFile(path: string): Promise<File> {
    // Fetch the file as an array buffer
    const response = await fetch(new Request(path));
    const buffer = await response.arrayBuffer();
    // Load the buffer into a Rive file
    return this.rive.load(new Uint8Array(buffer)) as Promise<File>;
  }

  /**
   * Loads a Rive file and initializes its artboard and state machine.
   *
   * This method handles fetching, loading, and initializing a Rive file.
   * It also caches the loaded asset to prevent duplicate loading.
   *
   * @param {string} path - URL path to the Rive file
   * @param {Object} extendedOptions - Additional options for the loaded asset
   * @param {string} extendedOptions.id - Unique identifier for the animation
   * @param {string} extendedOptions.type - Type of animation (BODY, HAIR, etc.)
   * @param {number} extendedOptions.layer - Z-index for rendering order
   * @returns {Promise<LoadedRiveAsset>} Promise resolving to the loaded asset
   * @throws {Error} If loading fails
   */
  async loadFile(
    path: string,
    extendedOptions: { id: string; type: string; layer: number },
  ): Promise<LoadedRiveAsset> {
    // Return cached asset if available
    if (this.fileCache.has(path)) {
      return this.fileCache.get(path)!;
    }

    try {
      // Load the file from the URL
      const sourceFile = await this.fetchAndLoadFile(path);
      // Get the default artboard from the file
      const artboard = sourceFile?.defaultArtboard();
      // Initialize the state machine with type-specific setup
      const stateMachine = this.initializeStateMachine(
        artboard,
        extendedOptions,
      );

      // Create a loaded asset object with all necessary properties
      const loadedAsset = this.createLoadedAsset(
        extendedOptions,
        artboard,
        stateMachine,
      );

      // Add the loaded components to their respective collections
      this.addToCollections(sourceFile, artboard, stateMachine, loadedAsset);
      // Cache the loaded asset for future use
      this.fileCache.set(path, loadedAsset);

      return loadedAsset;
    } catch (error) {
      console.error(`Error loading file from path: ${path}`, error);
      throw error;
    }
  }

  /**
   * Initializes a state machine for an artboard with type-specific setup.
   *
   * Different animation types require different initialization:
   * - HAIR type: Sets up hair color and hair pinch inputs
   *
   * @param {Artboard} artboard - The artboard to create a state machine for
   * @param {Object} options - Options affecting initialization
   * @param {string} options.type - Type of animation (BODY, HAIR, etc.)
   * @returns {ExtendedStateMachineInstance} The initialized state machine
   * @private
   */
  private initializeStateMachine(
    artboard: Artboard,
    options: { type: string },
  ): ExtendedStateMachineInstance {
    // Create a new state machine instance from the artboard
    const stateMachine = new this.rive.StateMachineInstance(
      artboard.stateMachineByIndex(0),
      artboard,
    ) as ExtendedStateMachineInstance;

    // Advance the state machine to initialize it
    stateMachine.advance(0);

    // For HAIR type animations, set up the hair customization inputs
    if (options.type === 'HAIR') {
      stateMachine.hairColorInput = stateMachine.input(1).asNumber();
      stateMachine.hairPinchInput = stateMachine.input(2).asNumber();
    }

    return stateMachine;
  }

  /**
   * Creates a loaded asset object with all necessary properties.
   *
   * @param {Object} options - Base options for the asset
   * @param {string} options.id - Unique identifier for the animation
   * @param {string} options.type - Type of animation (BODY, HAIR, etc.)
   * @param {number} options.layer - Z-index for rendering order
   * @param {Artboard} artboard - The loaded artboard
   * @param {ExtendedStateMachineInstance} stateMachine - The initialized state machine
   * @returns {LoadedRiveAsset} The complete loaded asset object
   * @private
   */
  private createLoadedAsset(
    options: { id: string; type: string; layer: number },
    artboard: Artboard,
    stateMachine: ExtendedStateMachineInstance,
  ): LoadedRiveAsset {
    // Get the bounce trigger input (used for animation effects)
    const bounceTrigger = stateMachine.input(0).asTrigger();

    // Create and return the loaded asset object
    return {
      ...options,
      artboard,
      stateMachine,
      bounceTrigger,
      hairPinchInput: stateMachine.hairPinchInput || null,
      hairColorInput: stateMachine.hairColorInput || null,
    };
  }

  /**
   * Adds loaded components to their respective collections.
   *
   * @param {File} sourceFile - The loaded Rive file
   * @param {Artboard} artboard - The loaded artboard
   * @param {ExtendedStateMachineInstance} stateMachine - The initialized state machine
   * @param {LoadedRiveAsset} loadedAsset - The complete loaded asset
   * @private
   */
  private addToCollections(
    sourceFile: File,
    artboard: Artboard,
    stateMachine: ExtendedStateMachineInstance,
    loadedAsset: LoadedRiveAsset,
  ): void {
    this.loadedFiles.push(sourceFile);
    this.loadedArtboards.push(artboard);
    this.loadedStateMachines.push(stateMachine);
    this.loadedLayers.push(loadedAsset);
  }

  /**
   * Loads multiple animations in parallel.
   *
   * This method preserves the BODY animation and replaces all other animations
   * with the new ones provided. It loads all animations concurrently for efficiency.
   *
   * @param {Animation[]} animations - Array of animations to load
   * @returns {Promise<void>} Promise that resolves when all animations are loaded
   */
  async loadAnimations(animations: Animation[]) {
    if (!animations?.length) return;

    // Keep only the BODY animation from currently loaded layers
    const newLayers = this.loadedLayers.filter(
      (layer) => layer.type === 'BODY',
    );

    // Create promises for loading each animation
    const promises = animations.map(async (animation) => {
      const loadedAsset = await this.loadFile(animation.cdnURL, {
        id: animation.id,
        type: animation.type,
        layer: animation.layer,
      });
      newLayers.push(loadedAsset);
    });

    try {
      // Wait for all animations to load
      await Promise.all(promises);
      // Replace the loaded layers with the new set
      this.loadedLayers = newLayers;
    } catch (error) {
      console.error('Error loading animations:', error);
    }
  }

  /**
   * Sorts loaded layers by their z-index and triggers bounce animation.
   *
   * This method ensures layers are rendered in the correct order based on their
   * layer property (z-index). It also removes any duplicate layers and triggers
   * a bounce animation effect on all layers.
   *
   * @returns {Promise<void>} Promise that resolves when sorting is complete
   */
  async sortLayers() {
    // Remove duplicate layers (keeping the first occurrence)
    const uniqueLayers = uniq(this.loadedLayers);
    // Sort layers by their z-index (layer property)
    this.sortedLayers = sortBy(uniqueLayers, (layer) => layer.layer);
    // Trigger bounce animation on all layers
    this.bounce();
  }

  /**
   * Triggers the bounce animation effect on all loaded layers.
   *
   * This method fires the bounce trigger on each layer's state machine,
   * creating a visual bounce effect in the animation.
   */
  bounce() {
    this.loadedLayers.forEach((layer) => {
      layer.bounceTrigger?.fire();
    });
  }

  /**
   * Cleans up all resources to prevent memory leaks.
   *
   * This method properly disposes of all loaded files, artboards, and state machines.
   * It also clears the collections and cache to release memory.
   */
  cleanup() {
    this.cleanupFiles();
    this.cleanupArtboards();
    this.cleanupStateMachines();
    this.loadedLayers = [];
    this.sortedLayers = [];
    this.fileCache.clear();
  }

  /**
   * Cleans up loaded Rive files.
   * @private
   */
  private cleanupFiles() {
    this.loadedFiles.forEach((file) => file?.delete());
    this.loadedFiles = [];
  }

  /**
   * Cleans up loaded artboards.
   * @private
   */
  private cleanupArtboards() {
    this.loadedArtboards.forEach((artboard) => artboard?.delete());
    this.loadedArtboards = [];
  }

  /**
   * Cleans up loaded state machines.
   * @private
   */
  private cleanupStateMachines() {
    this.loadedStateMachines.forEach((stateMachine) => stateMachine?.delete());
    this.loadedStateMachines = [];
  }
}
